import { type ComponentProps, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import requireValue from 'utils/requireValue';

type TMessageValues = ComponentProps<typeof FormattedMessage>['values'];

/** The parts react-intl passes to a rich text tag function. */
type TChunks = ReactNode[];

interface IFormattedHTMLMessageProps {
  id: string;
  defaultMessage?: string;
  values?: TMessageValues;
}

/** The text of an `<a>` tag: `href=<url> <label>`; anything else throws as `chunk.substr(...)` did. */
const linkTextOf = (chunks: TChunks): string => {
  const chunk = requireValue(chunks[0], 'substr');
  if (typeof chunk !== 'string') throw new TypeError('chunk.substr is not a function');
  return chunk;
};

// The keys are the stringified chunks, as React stringifies any key.
const FormattedHTMLMessage = ({ id, defaultMessage = ' ', values }: IFormattedHTMLMessageProps) => {
  return (
    <FormattedMessage
      id={id}
      defaultMessage={defaultMessage}
      values={{
        p: (...chunks: TChunks[]) => <p key={String(chunks)}>{chunks}</p>,
        ul: (...chunks: TChunks[]) => <ul key={String(chunks)}>{chunks}</ul>,
        ol: (...chunks: TChunks[]) => <ol key={String(chunks)}>{chunks}</ol>,
        li: (chunks: TChunks) => {
          return <li key={String(chunks)}>{chunks}</li>;
        },
        a: (chunks: TChunks) => {
          const chunk = linkTextOf(chunks);
          const href = chunk.substr(5, chunk.indexOf(' ') - 5);
          const label = chunk.substr(chunk.indexOf(' ') + 1);
          return (
            <a href={href} key={String(chunks)} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          );
        },
        ...values,
      }}
    />
  );
};

export default FormattedHTMLMessage;
